import React, { useState } from 'react';
import Button from './UI/Button';
import './SubscriptionForm.css';

const METHODS = [
  { id: 'email', label: 'Email', icon: '📧' },
  { id: 'sms', label: 'Text Message (SMS)', icon: '📱' },
  { id: 'push', label: 'Push Notification', icon: '🔔' },
];

export default function SubscriptionForm({ topic, citySlug, existingSubscription, onSubmit, onUnsubscribe, onCancel }) {
  const isEditing = !!existingSubscription;

  const [step, setStep] = useState(1);
  const [methods, setMethods] = useState(
    isEditing ? existingSubscription.methods : ['email']
  );
  const [email, setEmail] = useState(
    isEditing ? existingSubscription.contact.email || '' : ''
  );
  const [phone, setPhone] = useState(
    isEditing ? existingSubscription.contact.phone || '' : ''
  );
  const [address, setAddress] = useState(
    isEditing ? existingSubscription.address || '' : ''
  );
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);

  function toggleMethod(id) {
    setMethods((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  }

  function validate() {
    const errs = {};
    if (methods.length === 0) errs.methods = 'Select at least one notification method.';
    if (methods.includes('email') && !email.trim()) errs.email = 'Email address is required.';
    if (methods.includes('email') && email && !/\S+@\S+\.\S+/.test(email)) errs.email = 'Enter a valid email address.';
    if (methods.includes('sms') && !phone.trim()) errs.phone = 'Phone number is required for SMS.';
    if (topic.requiresAddress && !address.trim()) errs.address = 'Address is required for this alert type.';
    return errs;
  }

  function handleNext() {
    if (step === 1) { setStep(2); return; }
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit({ methods, contact: { email, phone }, address });
    setDone(true);
  }

  if (done) {
    return (
      <div className="subform-done">
        <div className="subform-done-icon">✅</div>
        <h3>You're subscribed!</h3>
        <p>
          You'll receive <strong>{topic.name}</strong> alerts via{' '}
          {methods.join(', ')}.
        </p>
        <Button variant="primary" onClick={onCancel} fullWidth>
          Back to Alerts
        </Button>
      </div>
    );
  }

  return (
    <div className="subform">
      {/* Topic summary */}
      <div className="subform-topic">
        <span className="subform-topic-icon">{topic.icon}</span>
        <div>
          <div className="subform-topic-name">{topic.name}</div>
          <div className="subform-topic-desc">{topic.description}</div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="subform-steps">
        <div className={`subform-step${step >= 1 ? ' active' : ''}`}>
          <span>1</span> Alert Options
        </div>
        <div className="subform-step-divider" />
        <div className={`subform-step${step >= 2 ? ' active' : ''}`}>
          <span>2</span> Contact Info
        </div>
      </div>

      {step === 1 && (
        <div className="subform-section">
          {topic.requiresAddress && (
            <div className="subform-field">
              <label className="subform-label">Your Street Address *</label>
              <input
                className={`subform-input${errors.address ? ' has-error' : ''}`}
                type="text"
                placeholder="e.g. 123 Main St"
                value={address}
                onChange={(e) => { setAddress(e.target.value); setErrors((p) => ({ ...p, address: '' })); }}
              />
              {errors.address && <div className="subform-error">{errors.address}</div>}
              <div className="subform-hint">Used to match alerts to your specific location.</div>
            </div>
          )}

          <div className="subform-field">
            <label className="subform-label">Notification Method *</label>
            <div className="subform-methods">
              {METHODS.map((m) => (
                <label
                  key={m.id}
                  className={`subform-method${methods.includes(m.id) ? ' selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={methods.includes(m.id)}
                    onChange={() => { toggleMethod(m.id); setErrors((p) => ({ ...p, methods: '' })); }}
                  />
                  <span className="subform-method-icon">{m.icon}</span>
                  <span className="subform-method-label">{m.label}</span>
                  {methods.includes(m.id) && <span className="subform-check">✓</span>}
                </label>
              ))}
            </div>
            {errors.methods && <div className="subform-error">{errors.methods}</div>}
            {methods.includes('push') && (
              <div className="subform-hint">⚠ Push notifications require a compatible browser and permission.</div>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="subform-section">
          {methods.includes('email') && (
            <div className="subform-field">
              <label className="subform-label">Email Address *</label>
              <input
                className={`subform-input${errors.email ? ' has-error' : ''}`}
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: '' })); }}
              />
              {errors.email && <div className="subform-error">{errors.email}</div>}
            </div>
          )}
          {methods.includes('sms') && (
            <div className="subform-field">
              <label className="subform-label">Phone Number *</label>
              <input
                className={`subform-input${errors.phone ? ' has-error' : ''}`}
                type="tel"
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, phone: '' })); }}
              />
              {errors.phone && <div className="subform-error">{errors.phone}</div>}
            </div>
          )}
          <div className="subform-privacy">
            🔒 Your contact information is only used to deliver the alerts you select. We never share or sell your data.
          </div>
        </div>
      )}

      <div className="subform-actions">
        {step === 2 && (
          <Button variant="ghost" onClick={() => setStep(1)}>← Back</Button>
        )}
        {isEditing && step === 1 && (
          <Button variant="danger" onClick={onUnsubscribe}>Unsubscribe</Button>
        )}
        <Button variant="primary" onClick={handleNext} fullWidth={step === 2}>
          {step === 1 ? 'Next →' : isEditing ? 'Save Changes' : 'Subscribe'}
        </Button>
      </div>
    </div>
  );
}
